import { useState } from "react";
import { usePageTracking } from "@/hooks/use-track";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/status-badge";
import { EmptyState } from "@/components/empty-state";
import { TableSkeleton } from "@/components/loading-skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ClipboardList, Plus, Search, Clock, Filter, Pencil, Download, Zap, Trash2,
} from "lucide-react";
import { HelpTip } from "@/components/help-tip";
import { usePageTour } from "@/components/ProductTour";
import { TutorialTip } from "@/components/TutorialTip";

// Mirrors the shape returned by GET /api/site-surveys. Kept local to avoid a
// new shared type — the survey list view only needs the columns shown below.
interface SiteSurveyRow {
  id: string;
  title: string;
  status: string; // 'draft' | 'completed'
  projectAddress: string | null;
  projectType: string | null;
  reportObjectPath: string | null;
  updatedAt: string | null;
  createdAt: string | null;
  data?: {
    project?: {
      clientName?: string;
      projectAddress?: string;
      projectType?: string;
      description?: string;
    };
  } | null;
}

export default function SiteSurveysPage() {
  usePageTracking("Site Surveys");
  usePageTour("site-surveys");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [confirmDelete, setConfirmDelete] = useState<SiteSurveyRow | null>(null);

  const { data: surveys, isLoading } = useQuery<SiteSurveyRow[]>({
    queryKey: ["/api/site-surveys"],
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/site-surveys/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/site-surveys"] });
      toast({ title: "Site survey deleted" });
      setConfirmDelete(null);
    },
    onError: (err: any) => {
      toast({ title: err.message || "Failed to delete site survey", variant: "destructive" });
    },
  });

  // Download the previously-generated PDF report straight from object storage.
  // The PATCH on the survey saved `reportObjectPath` when the user pressed
  // "Send to Auto Quote", so completed surveys can be re-downloaded without
  // regenerating the PDF.
  function handleDownload(survey: SiteSurveyRow) {
    if (!survey.reportObjectPath) {
      toast({
        title: "No report available",
        description: "Open the survey and use 'Send to Auto Quote' to generate the report PDF.",
        variant: "destructive",
      });
      return;
    }
    const a = document.createElement("a");
    a.href = survey.reportObjectPath;
    a.download = `site-visit-report-${survey.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  // Re-trigger the same Site Survey → Auto Quote handover used inside the
  // detail page, but driven from the list. We replay the existing report PDF
  // (already stored on the survey) so the model gets the embedded photos +
  // notes exactly once. Photos are deliberately NOT attached separately — the
  // report PDF already inlines them.
  function handleSendToAutoQuote(survey: SiteSurveyRow) {
    if (!survey.reportObjectPath) {
      toast({
        title: "No report available",
        description: "Open the survey, finish capturing notes, then press 'Send to Auto Quote' to generate the report PDF first.",
        variant: "destructive",
      });
      return;
    }
    const project = survey.data?.project ?? {
      projectAddress: survey.projectAddress ?? undefined,
      projectType: survey.projectType ?? undefined,
    };
    sessionStorage.setItem(
      "site-survey-handover",
      JSON.stringify({
        surveyId: survey.id,
        reportObjectPath: survey.reportObjectPath,
        reportFileName: `site-visit-report-${survey.id}.pdf`,
        project,
      })
    );
    navigate("/quotes/new?from=site-survey");
  }

  const filtered = surveys?.filter((s) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      s.title?.toLowerCase().includes(q) ||
      s.projectAddress?.toLowerCase().includes(q) ||
      s.projectType?.toLowerCase().includes(q) ||
      s.data?.project?.clientName?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === "all" || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold flex items-center gap-2 flex-wrap" data-testid="text-site-surveys-title">
            Site Surveys
            <HelpTip
              testId="button-help-site-surveys"
              content="Every site visit you capture is saved here. Open one to keep editing your notes, download the PDF report, or send it to Auto Quote to generate a fresh quote from the visit."
            />
          </h1>
          <p className="text-muted-foreground">Voice notes, photos and measurements from every site visit</p>
        </div>
        <Button onClick={() => navigate("/quotes/survey")} data-testid="button-new-site-survey">
          <Plus className="h-4 w-4 mr-2" />
          New Site Survey
        </Button>
      </div>

      <TutorialTip id="site-surveys-intro" title="Turn site visits into quotes">
        Capture rooms, photos and voice notes on site, then send the report
        straight to Auto Quote. Your priced quote is built from the visit
        without re-typing a thing.
      </TutorialTip>

      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by address, project type or client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-site-surveys"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="select-survey-status-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={5} />
            </div>
          ) : !filtered || filtered.length === 0 ? (
            <EmptyState
              icon={ClipboardList}
              title="No site surveys yet"
              description={
                searchQuery || statusFilter !== "all"
                  ? "Try adjusting your search or filters"
                  : "Capture your first site visit to get started"
              }
              actionLabel="New Site Survey"
              onAction={() => navigate("/quotes/survey")}
            />
          ) : (
            <div className="divide-y">
              {filtered.map((survey) => {
                const hasReport = !!survey.reportObjectPath;
                const dateStr = survey.updatedAt || survey.createdAt;
                return (
                  <div
                    key={survey.id}
                    className="flex items-center gap-4 p-4 hover-elevate cursor-pointer group"
                    onClick={() => navigate(`/quotes/survey/${survey.id}`)}
                    data-testid={`row-site-survey-${survey.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{survey.title || "Untitled site visit"}</span>
                        <StatusBadge status={survey.status || "draft"} />
                      </div>
                      <div className="text-sm text-muted-foreground truncate">
                        {survey.data?.project?.clientName || "No client"}
                        {survey.projectAddress && ` • ${survey.projectAddress}`}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {survey.projectType?.replace(/_/g, " ") || "Unspecified project type"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="md:opacity-0 md:group-hover:opacity-100 transition-opacity h-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/quotes/survey/${survey.id}`);
                        }}
                        data-testid={`button-edit-site-survey-${survey.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5 md:mr-1.5" />
                        <span className="hidden md:inline">Edit</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="md:opacity-0 md:group-hover:opacity-100 transition-opacity h-8"
                        disabled={!hasReport}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(survey);
                        }}
                        data-testid={`button-download-site-survey-${survey.id}`}
                      >
                        <Download className="h-3.5 w-3.5 md:mr-1.5" />
                        <span className="hidden md:inline">Download</span>
                      </Button>
                      <Button
                        size="sm"
                        className="md:opacity-0 md:group-hover:opacity-100 transition-opacity h-8"
                        disabled={!hasReport}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendToAutoQuote(survey);
                        }}
                        data-testid={`button-send-autoquote-${survey.id}`}
                      >
                        <Zap className="h-3.5 w-3.5 md:mr-1.5" />
                        <span className="hidden md:inline">Auto Quote</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="md:opacity-0 md:group-hover:opacity-100 transition-opacity h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete(survey);
                        }}
                        data-testid={`button-delete-site-survey-${survey.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      <div className="text-right hidden sm:block min-w-[88px]">
                        <div className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                          <Clock className="h-3 w-3" />
                          {dateStr ? new Date(dateStr).toLocaleDateString("en-GB") : "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this site survey?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the survey and its notes. The report PDF
              already downloaded or sent to Auto Quote is unaffected. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-survey">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-survey"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
